import { useCallback, useEffect, useMemo, useState } from "react";
import { EMPTY_CONTEXT, EMPTY_FILTERS } from "../model/commandeConstants";
import { countActiveFilters } from "../model/commandeListQuery";

function readFilterOf(filterType) {
    if (filterType === "READ") return true;
    return filterType === "UNREAD" ? false : null;
}

function favoriteFilterOf(filterType) {
    if (filterType === "FAVORITE") return true;
    return filterType === "NOT_FAVORITE" ? false : null;
}

function statutsOf(filterType) {
    if (filterType === "COMMAND_TO_VALIDATE") return ["A_VALIDER"];
    if (filterType === "COMMAND_VALIDATED") return ["VALIDE"];
    return [];
}

// Filtres de la liste des commandes, page courante et libellé actif. Un libellé applique son EHPAD, sa tournée et
// son filtre ; un second clic le retire. Tout changement de filtre ramène à la première page.
export function useCommandeFilters(context) {
    const { filters = EMPTY_FILTERS } = context || EMPTY_CONTEXT;
    const [selectedDateDebut] = useState(null);
    const [selectedDateFin] = useState(null);
    const [selectedStatuts, setSelectedStatuts] = useState([]);
    const [selectedEhpadId, setSelectedEhpadId] = useState(null);
    const [selectedTournees, setSelectedTournees] = useState([]);
    const [patientSearch, setPatientSearch] = useState("");
    const [patientDateNaissance] = useState(null);
    const [selectedClassificationPatient] = useState(filters.classificationPatient ?? null);
    const [selectedClassificationMedicament] = useState(null);
    const [selectedSource] = useState(filters.source ?? null);
    const [readFilter, setReadFilter] = useState(null);
    const [favoriteFilter, setFavoriteFilter] = useState(null);
    const [medicamentSearch] = useState("");
    const [activeLabelKey, setActiveLabelKey] = useState(null);
    const [page, setPage] = useState(0);
    const serviceKey = context?.serviceKey ?? null;

    const values = {
        filters, serviceKey, selectedDateDebut, selectedDateFin, selectedStatuts, selectedEhpadId, selectedTournees,
        patientSearch, patientDateNaissance, selectedClassificationPatient, selectedClassificationMedicament,
        selectedSource, readFilter, favoriteFilter, medicamentSearch,
    };
    const listFilterKey = useMemo(() => JSON.stringify({
        selectedDateDebut, selectedDateFin, selectedEhpadId, selectedTournees, selectedStatuts, patientSearch,
        patientDateNaissance, selectedClassificationPatient, selectedClassificationMedicament, selectedSource,
        readFilter, favoriteFilter, medicamentSearch, serviceKey, filters,
    }), [
        selectedDateDebut, selectedDateFin, selectedEhpadId, selectedTournees, selectedStatuts, patientSearch,
        patientDateNaissance, selectedClassificationPatient, selectedClassificationMedicament, selectedSource,
        readFilter, favoriteFilter, medicamentSearch, serviceKey, filters,
    ]);

    useEffect(() => { setPage(0); }, [selectedDateDebut, selectedDateFin, selectedEhpadId, selectedTournees, selectedStatuts, patientSearch, patientDateNaissance, selectedClassificationPatient, selectedClassificationMedicament, selectedSource, readFilter, favoriteFilter, medicamentSearch]);

    const clearLabelSelection = useCallback(() => {
        setActiveLabelKey(null);
        setSelectedEhpadId(null);
        setSelectedTournees([]);
        setReadFilter(null);
        setFavoriteFilter(null);
        setSelectedStatuts([]);
        setPage(0);
    }, []);

    const selectCustomLabel = useCallback((label) => {
        if (activeLabelKey === `label:${label.id}`) {
            clearLabelSelection();
            return;
        }
        setActiveLabelKey(`label:${label.id}`);
        setSelectedEhpadId(label.ehpadId || null);
        setSelectedTournees(label.tourneeName ? [label.tourneeName] : []);
        setReadFilter(readFilterOf(label.filterType));
        setFavoriteFilter(favoriteFilterOf(label.filterType));
        setSelectedStatuts(statutsOf(label.filterType));
        setPage(0);
    }, [activeLabelKey, clearLabelSelection]);

    return {
        ...values,
        setSelectedStatuts, setSelectedEhpadId, setSelectedTournees, setPatientSearch, setReadFilter, setFavoriteFilter,
        activeLabelKey, page, setPage, listFilterKey, activeFilterCount: countActiveFilters(values),
        clearLabelSelection, selectCustomLabel,
    };
}

// Liens entrants (?tournee=, ?ehpadId=, ?commandeId=), au chargement, depuis la recherche globale ou l'historique.
export function useNavigationSearch(f, openCommande) {
    const { setSelectedTournees, setSelectedEhpadId } = f;
    useEffect(() => {
        const applyNavigationSearch = search => {
            const params = new URLSearchParams(search || "");
            const tourneeParams = params.getAll("tournee").filter(Boolean);
            const ehpadParam = Number(params.get("ehpadId"));
            const commandeParam = Number(params.get("commandeId"));

            setSelectedTournees(tourneeParams);
            setSelectedEhpadId(Number.isFinite(ehpadParam) && ehpadParam > 0 ? ehpadParam : null);
            if (Number.isFinite(commandeParam) && commandeParam > 0) openCommande(commandeParam);
        };

        const handleSearchNavigation = event => {
            const path = event.detail?.path;
            if (!path) return;
            const queryIndex = path.indexOf("?");
            applyNavigationSearch(queryIndex >= 0 ? path.slice(queryIndex) : "");
        };
        const handlePopState = () => applyNavigationSearch(globalThis.location?.search || "");

        applyNavigationSearch(globalThis.location?.search || "");
        globalThis.addEventListener("global-search:navigate", handleSearchNavigation);
        globalThis.addEventListener("popstate", handlePopState);
        return () => {
            globalThis.removeEventListener("global-search:navigate", handleSearchNavigation);
            globalThis.removeEventListener("popstate", handlePopState);
        };
    // Écouteurs posés une seule fois, comme avant le découpage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
